import * as React from 'react';

import { GlobalMarketDataShape } from '@blockworks/platform/api/research';
import type { PostType } from '@blockworks/platform/api/strapi';
import { useTrack } from '@blockworks/platform/services';

import SectionHeader from '@/components/display/headers/section-header';
import { TokenTransparencyDescriptiveContent } from '@/components/pages/token-transparency/components/token-transparency-descriptive-content';
import { TokenTransparencyHeader } from '@/components/pages/token-transparency/components/token-transparency-header';
import { TokenTransparencyRightRail } from '@/components/pages/token-transparency/components/token-transparency-right-rail';
import { TokenTransparencyTable } from '@/components/pages/token-transparency/components/token-transparency-table';
import type { TokenTransparencyWithTotals } from '@/components/pages/token-transparency/token-transparency-page-static-props';
import NewsRecirculationSection from '@/components/sections/content/news-recirculation-section';
import { WordpressPage } from '@/utils/functions/wordpress/fetch-pages';

type TokenTransparencyPageLayoutProps = {
    tokenTransparencyReports: TokenTransparencyWithTotals[];
    recentResearch: PostType;
    globalMarketHistory: GlobalMarketDataShape[];
    assets: { [key: string]: { imageUrl: string } };
    pageContent: WordpressPage | null;
};

const TokenTransparencyLayout = (props: TokenTransparencyPageLayoutProps) => {
    const { pageContent, globalMarketHistory, tokenTransparencyReports, assets, recentResearch } = props;

    const track = useTrack();

    return (
        <main className="border-gray-200 w-full">
            <section className="flex flex-row flex-wrap xl:flex-nowrap overflow-hidden">
                <div className="h-full p-0 xl:border-r border-b xl:border-b-0 w-full">
                    <TokenTransparencyHeader />
                    <div className="flex flex-col w-full min-h-[700px]">
                        <TokenTransparencyTable
                            tokenTransparencyReports={tokenTransparencyReports}
                            assets={assets}
                            track={track}
                        />
                        <section className="px-4 md:px-10">
                            <TokenTransparencyDescriptiveContent pageContent={pageContent} />
                        </section>
                    </div>
                </div>
                <div className="flex flex-wrap xl:flex-col">
                    <TokenTransparencyRightRail
                        recentResearch={recentResearch}
                        globalMarketHistory={globalMarketHistory}
                    />
                </div>
            </section>
            <section>
                <SectionHeader
                    className="border-b border-t"
                    title="related news"
                    description=""
                    linkTitle={
                        <span className="flex align-middle">
                            more from news
                            <svg
                                width={8}
                                height={4}
                                viewBox="0 0 8 4"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="ml-1 mt-1.5"
                                preserveAspectRatio="none"
                            >
                                <path d="M6.005 1.5H0V2.5H6.005V4L8 2L6.005 0V1.5Z" fill="#5637CD" />
                            </svg>
                        </span>
                    }
                    linkUrl="/news"
                />
                <div>
                    <NewsRecirculationSection
                        algoliaSearchKeyword={tokenTransparencyReports
                            .filter(tt => tt.tokenSymbol)
                            .map(tt => tt.tokenSymbol)
                            .join(' OR ')}
                        style="horizontal"
                        count={8}
                        page={1}
                        paddingClassName="p-8"
                        className="p-0"
                    />
                </div>
            </section>
        </main>
    );
};

export default TokenTransparencyLayout;
